import { CampaignPaginationItem } from '@common/types/campaign';

const DEFAULT_SIBLING_COUNT = 1;
const DEFAULT_BOUNDARY_COUNT = 1;
export const MOBILE_SIBLING_COUNT = 0;
export const MOBILE_BOUNDARY_COUNT = 1;

export const ARROW_SCALE = 3;
export const CONTROL_SIZE = 12 * ARROW_SCALE;
export const GAP_BETWEEN_ELEMENTS = '8px';

function range(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

/**
 * Returns the page numbers and ellipsis markers shown in the pager
 * Matches MUI Pagination defaults (siblingCount=1, boundaryCount=1)
 *
 * @param page - the current page
 * @param pageCount - the total number of pages
 * @param siblingCount - the number of siblings to show
 * @param boundaryCount - the number of boundaries to show
 * @returns the visible pagination pages
 */
export function getVisibleCampaignPaginationPages(
  page: number,
  pageCount: number,
  siblingCount = DEFAULT_SIBLING_COUNT,
  boundaryCount = DEFAULT_BOUNDARY_COUNT
): Array<number | 'start-ellipsis' | 'end-ellipsis'> {
  const totalPageNumbers = siblingCount * 2 + 3 + boundaryCount * 2;

  if (pageCount <= totalPageNumbers) {
    return range(1, pageCount);
  }

  const leftSiblingIndex = Math.max(page - siblingCount, boundaryCount + 1);
  const rightSiblingIndex = Math.min(
    page + siblingCount,
    pageCount - boundaryCount
  );
  const shouldShowLeftEllipsis = leftSiblingIndex > boundaryCount + 2;
  const shouldShowRightEllipsis =
    rightSiblingIndex < pageCount - boundaryCount - 1;

  if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    const leftItemCount = 3 + 2 * siblingCount;

    return [
      ...range(1, leftItemCount),
      'end-ellipsis',
      ...range(pageCount - boundaryCount + 1, pageCount),
    ];
  }

  if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
    const rightItemCount = 3 + 2 * siblingCount;

    return [
      ...range(1, boundaryCount),
      'start-ellipsis',
      ...range(pageCount - rightItemCount + 1, pageCount),
    ];
  }

  if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    return [
      ...range(1, boundaryCount),
      'start-ellipsis',
      ...range(leftSiblingIndex, rightSiblingIndex),
      'end-ellipsis',
      ...range(pageCount - boundaryCount + 1, pageCount),
    ];
  }

  return range(1, pageCount);
}

/**
 * Builds pagination control metadata for the campaign list pager.
 *
 * @param page - the current page
 * @param pageCount - the total number of pages
 * @param disabled - whether the pagination is disabled
 * @returns the pagination items
 */
export function buildCampaignPaginationItems(
  page: number,
  pageCount: number,
  disabled = false,
  siblingCount = DEFAULT_SIBLING_COUNT,
  boundaryCount = DEFAULT_BOUNDARY_COUNT
): CampaignPaginationItem[] {
  if (pageCount <= 0) {
    return [];
  }

  const currentPage = Math.min(Math.max(page, 1), pageCount);
  const visiblePages = getVisibleCampaignPaginationPages(
    currentPage,
    pageCount,
    siblingCount,
    boundaryCount
  );

  return [
    {
      disabled: disabled || currentPage <= 1,
      type: 'previous',
    },
    ...visiblePages.map((entry) => {
      if (entry === 'start-ellipsis' || entry === 'end-ellipsis') {
        return { type: entry };
      }

      return {
        disabled,
        page: entry,
        selected: entry === currentPage,
        type: 'page' as const,
      };
    }),
    {
      disabled: disabled || currentPage >= pageCount,
      type: 'next',
    },
  ];
}

/**
 * getPageChangeHandler
 *
 * Handles the page change
 * @param item - the item
 * @param page - the page
 * @param isPageChangeDisabled - whether the page change is disabled
 * @param runPageChange - the function to run the page change
 */
export function getPageChangeHandler(
  item: CampaignPaginationItem,
  page: number,
  isPageChangeDisabled: boolean,
  runPageChange: (newPage: number) => void
) {
  return (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (isPageChangeDisabled) {
      return;
    }

    if (item.type === 'previous') {
      void runPageChange(page - 1);
      return;
    }

    if (item.type === 'next') {
      void runPageChange(page + 1);
      return;
    }

    if (item.type === 'page' && item.page !== page) {
      void runPageChange(item.page);
    }
  };
}
/**
 * arePageItemsEquivalent
 *
 * Compares two arrays of CampaignPaginationItems to determine if they are equivalent.
 * Items are considered equivalent if they have the same type and, for page items, the same page number.
 *
 * @param left - the first array of items
 * @param right - the second array of items
 * @returns true if the arrays are equivalent, false otherwise
 */
export function arePageItemsEquivalent(
  left: CampaignPaginationItem[],
  right: CampaignPaginationItem[]
): boolean {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((item, index) => {
    const other = right[index];

    if (item.type !== other.type) {
      return false;
    }

    if (item.type === 'page' && other.type === 'page') {
      return item.page === other.page;
    }

    return true;
  });
}
